<template>
  <div v-if="!hasPermission('List NFSe')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar/>

    <div class="container-xxl">
      <div class="card mb-5">
        <div class="card-header border-0 pt-6">
          <div class="card-title">
            <h3 class="fw-bold m-0">Notas Fiscais de Serviço</h3>
          </div>
          <div class="card-toolbar">
            <button v-if="hasPermission('Configure NFSe')" 
                    class="btn btn-light-primary me-2" 
                    @click="openSettings()">
              <i class="bi bi-gear"></i> Configurações
            </button>
            <button v-if="hasPermission('Generate NFSe')" 
                    class="btn btn-primary" 
                    @click="openNewNfse()">
              <i class="bi bi-plus-lg"></i> Nova NFSe
            </button>
          </div>
        </div>

        <div class="card-body pt-0">
          <!-- Filtros -->
          <div class="card mb-5 mb-xl-8">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1">Filtros</span>
              </h3>
            </div>
            <div class="card-body pt-0">
              <div class="row g-3">
                <div class="col-md-3">
                  <label class="form-label">Buscar</label>
                  <input v-model="filters.search" class="form-control" 
                         placeholder="Número, nome, documento..."
                         @keyup.enter="loadNfses()">
                </div>
                <div class="col-md-2">
                  <label class="form-label">Status</label>
                  <select v-model="filters.status" class="form-select">
                    <option value="">Todos</option>
                    <option v-for="status in statusList" :key="status.code" :value="status.code">
                      {{ status.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Data Início</label>
                  <input type="date" class="form-control" v-model="filters.start_date">
                </div>
                <div class="col-md-2">
                  <label class="form-label">Data Fim</label>
                  <input type="date" class="form-control" v-model="filters.end_date">
                </div>
                <div class="col-md-3 d-flex align-items-end gap-2">
                  <button @click="applyFilters()" class="btn btn-primary">
                    <i class="bi bi-search"></i> Filtrar
                  </button>
                  <button @click="clearFilters()" class="btn btn-light">
                    Limpar
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabela -->
          <div class="table-responsive">
            <table class="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr class="fw-bold text-muted">
                  <th class="min-w-100px">Número</th>
                  <th class="min-w-150px">Tomador</th>
                  <th class="min-w-100px">Valor</th>
                  <th class="min-w-100px">Status</th>
                  <th class="min-w-100px">Origem</th>
                  <th class="min-w-100px">Data</th>
                  <th class="min-w-100px text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="7" class="text-center py-10">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Carregando...</span>
                    </div>
                  </td>
                </tr>
                <tr v-else-if="nfses.length === 0">
                  <td colspan="7" class="text-center text-muted py-10">
                    <i class="bi bi-inbox fs-1"></i>
                    <div class="mt-3">Nenhuma NFSe encontrada</div>
                  </td>
                </tr>
                <tr v-for="nfse in nfses" :key="nfse.id_nfse">
                  <td>
                    <span class="text-dark fw-bold d-block fs-6">
                      {{ nfse.nfse_number || nfse.nfse_rps_number || '-' }}
                    </span>
                  </td>
                  <td>
                    <span class="text-dark fw-bold d-block fs-6">{{ nfse.nfse_taker_name }}</span>
                    <span class="text-muted fw-semibold d-block fs-7">{{ formatDocument(nfse.nfse_taker_document) }}</span>
                  </td>
                  <td>
                    <span class="text-dark fw-bold d-block fs-6">{{ formatCurrency(nfse.nfse_service_value) }}</span>
                  </td>
                  <td>
                    <span class="badge" :style="{ backgroundColor: nfse.status_color, color: '#fff' }">
                      {{ nfse.status_description }}
                    </span>
                  </td>
                  <td>
                    <span class="badge badge-light">{{ nfse.origin_description }}</span>
                  </td>
                  <td>
                    <span class="text-muted fw-semibold d-block fs-7">
                      {{ formatDate(nfse.nfse_emission_date || nfse.created_at) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button v-if="nfse.nfse_status === 'draft' && hasPermission('Generate NFSe')" 
                            class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" 
                            @click="editNfse(nfse)" title="Editar">
                      <i class="bi bi-pencil-square"></i>
                    </button>
                    <button v-if="nfse.nfse_status === 'draft' && hasPermission('Generate NFSe')" 
                            class="btn btn-icon btn-bg-light btn-active-color-success btn-sm me-1" 
                            @click="emitNfse(nfse)" 
                            title="Emitir">
                      <i class="bi bi-send"></i>
                    </button>
                    <button v-if="nfse.is_authorized || nfse.nfse_status === 'corrected'" 
                            class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" 
                            @click="downloadDanfse(nfse)" 
                            title="Baixar DANFSE">
                      <i class="bi bi-file-earmark-pdf"></i>
                    </button>
                    <button v-if="nfse.nfse_xml_nfse || nfse.nfse_xml_dps" 
                            class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" 
                            @click="downloadXml(nfse)" 
                            title="Baixar XML">
                      <i class="bi bi-file-earmark-code"></i>
                    </button>
                    <button v-if="nfse.can_cancel && hasPermission('Cancel NFSe')" 
                            class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1" 
                            @click="cancelNfse(nfse)" 
                            title="Cancelar">
                      <i class="bi bi-x-circle"></i>
                    </button>
                    <button v-if="nfse.nfse_status === 'draft'" 
                            class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm" 
                            @click="deleteNfse(nfse)" title="Excluir">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Pagination :data="paginationData" v-if="nfses.length > 0"/>
        </div>
      </div>
    </div>

    <!-- Modal Nova NFSe -->
    <ModalNfse ref="modalNfse" @saved="loadNfses()"/>
    
    <!-- Modal Configurações -->
    <ModalNfseSettings ref="modalSettings"/>
    
    <!-- Modal Cancelamento -->
    <ModalNfseCancellation ref="modalCancellation" @cancelled="loadNfses()"/>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Alert from "../../helpers/alert";
import Loader from "../../helpers/loader";
import Permission from "../../helpers/permission";
import Toolbar from "../../components/layouts/auth/Toolbar.vue";
import Pagination from "../../components/Pagination.vue";
import ModalNfse from "./ModalNfse.vue";
import ModalNfseSettings from "./ModalNfseSettings.vue";
import ModalNfseCancellation from "./ModalNfseCancellation.vue";

export default {
  name: "Nfse",
  components: {
    Toolbar,
    Pagination,
    ModalNfse,
    ModalNfseSettings,
    ModalNfseCancellation,
  },
  data() {
    return {
      nfses: [],
      statusList: [],
      loading: false,
      filters: {
        search: '',
        status: '',
        start_date: '',
        end_date: '',
      },
      pagination: {
        currentPage: 1,
        lastPage: 1,
        perPage: 15,
        total: 0,
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    paginationData() {
      return {
        current_page: this.pagination.currentPage,
        last_page: this.pagination.lastPage,
        per_page: this.pagination.perPage,
        total: this.pagination.total,
        data: this.nfses,
      };
    },
  },
  mounted() {
    this.loadStatusList();
    this.initPageFromQuery();
    this.loadNfses();
  },
  watch: {
    '$route.query.page'(newPage) {
      if (newPage) {
        this.pagination.currentPage = parseInt(newPage) || 1;
        this.loadNfses();
      }
    },
  },
  methods: {
    initPageFromQuery() {
      const page = this.$route.query.page;
      if (page) {
        this.pagination.currentPage = parseInt(page) || 1;
      }
    },
    async loadNfses() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.currentPage,
          per_page: this.pagination.perPage,
        };

        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.start_date) params.start_date = this.filters.start_date;
        if (this.filters.end_date) params.end_date = this.filters.end_date;

        const response = await http.get(`/places/${this.place.id_place}/nfse`, { params });
        
        this.nfses = response.data.data;
        this.pagination = {
          currentPage: response.data.current_page,
          lastPage: response.data.last_page,
          perPage: response.data.per_page,
          total: response.data.total,
        };
        
        this.updateQueryParams();
      } catch (e) {
        Alert.toastError('Erro ao carregar NFSes.');
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.pagination.currentPage = 1;
      this.$router.replace({ query: {} }).catch(() => {});
      this.loadNfses();
    },
    updateQueryParams() {
      const query = { ...this.$route.query };
      if (this.pagination.currentPage > 1) {
        query.page = this.pagination.currentPage.toString();
      } else {
        delete query.page;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    async loadStatusList() {
      try {
        const response = await http.get(`/places/${this.place.id_place}/nfse/status-list`);
        this.statusList = response.data;
      } catch (e) {
        console.error('Erro ao carregar status:', e);
      }
    },
    openNewNfse() {
      this.$refs.modalNfse.open();
    },
    editNfse(nfse) {
      this.$refs.modalNfse.open(nfse);
    },
    openSettings() {
      this.$refs.modalSettings.open();
    },
    async emitNfse(nfse) {
      const result = await Swal.fire({
        title: 'Emitir NFSe?',
        text: 'Tem certeza que deseja emitir esta NFSe? Esta ação não pode ser desfeita.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        confirmButtonText: 'Sim, emitir',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        Loader.show('Emitindo NFSe', 'Aguarde...');
        try {
          await http.post(`/places/${this.place.id_place}/nfse/${nfse.id_nfse}/emit`);
          Alert.toastSuccess('NFSe emitida com sucesso!');
          this.loadNfses();
        } catch (e) {
          Alert.toastError(e.response?.data?.error || 'Erro ao emitir NFSe.');
        } finally {
          Loader.close();
        }
      }
    },
    cancelNfse(nfse) {
      this.$refs.modalCancellation.open(nfse);
    },
    async deleteNfse(nfse) {
      const result = await Swal.fire({
        title: 'Excluir NFSe?',
        text: 'Tem certeza que deseja excluir este rascunho?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        try {
          await http.delete(`/places/${this.place.id_place}/nfse/${nfse.id_nfse}`);
          Alert.toastSuccess('NFSe excluída com sucesso!');
          this.loadNfses();
        } catch (e) {
          Alert.toastError(e.response?.data?.message || 'Erro ao excluir NFSe.');
        }
      }
    },
    async downloadDanfse(nfse) {
      Loader.show('Gerando DANFSE', 'Aguarde...');
      try {
        const response = await http.get(
          `/places/${this.place.id_place}/nfse/${nfse.id_nfse}/danfse`,
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `danfse_${nfse.nfse_number || nfse.id_nfse}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        Alert.toastError('Erro ao baixar DANFSE.');
      } finally {
        Loader.close();
      }
    },
    async downloadXml(nfse) {
      Loader.show('Baixando XML', 'Aguarde...');
      try {
        const response = await http.get(
          `/places/${this.place.id_place}/nfse/${nfse.id_nfse}/xml`,
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `nfse_${nfse.nfse_number || nfse.id_nfse}.xml`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        Alert.toastError('Erro ao baixar XML.');
      } finally {
        Loader.close();
      }
    },
    clearFilters() {
      this.filters = {
        search: '',
        status: '',
        start_date: '',
        end_date: '',
      };
      this.pagination.currentPage = 1;
      this.$router.replace({ query: {} }).catch(() => {});
      this.loadNfses();
    },
    hasPermission(permission) {
      return Permission.has(permission);
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('pt-BR');
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value || 0);
    },
    formatDocument(doc) {
      if (!doc) return '';
      const cleaned = doc.replace(/\D/g, '');
      if (cleaned.length === 11) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      } else if (cleaned.length === 14) {
        return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
      return doc;
    },
    setLoading(nfseId, action, isLoading) {
      this.loadingActions = {
        ...this.loadingActions,
        [nfseId]: { ...(this.loadingActions[nfseId] ?? {}), [action]: isLoading },
      };
    },
  },
};
</script>

<style scoped>
</style>
